import './NavMenu.css';

import { useAtomValue, useSetAtom } from 'jotai';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { Button, Drawer, IconButton, Menu, MenuItem } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import {
  locationsAtom,
  navbarHeightAtom,
  selectedSiteAtom,
  userNameAtom,
} from '../utils/AtomConfig';
import Constants from '../utils/Constants';
import { Locations } from './Locations';
import { Refiners } from './Refiners';
import { Sites } from './Sites';

export function NavMenu() {
  const setLocations = useSetAtom(locationsAtom);
  const setNavbarHeight = useSetAtom(navbarHeightAtom);
  const userName = useAtomValue(userNameAtom);
  const selectedSite = useAtomValue(selectedSiteAtom);
  const location = useLocation();

  const usernameEl = useRef(null);
  const [isShowMenu, setIsShowMenu] = useState(false);
  const [isShowDrawer, setIsShowDrawer] = useState(false);

  const isAuthenticated = !!userName;
  const isOnDocumentsPage = location.pathname === '/documents';

  const navbarRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (navbarRef.current) {
      // Set initial height
      setNavbarHeight(navbarRef.current.offsetHeight);

      // Add a resize observer to update height if the navbar content changes dynamically
      const resizeObserver = new ResizeObserver(
        (entries: ResizeObserverEntry[]) => {
          for (const entry of entries) {
            setNavbarHeight(entry.contentRect.height);
          }
        }
      );

      resizeObserver.observe(navbarRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [setNavbarHeight]);

  function logout() {
    setIsShowMenu(false);
    setLocations([]);
    window.location.href = Constants.LOGOUT_REDIRECT_URI;
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* AppBar */}
      <AppBar position='fixed' ref={navbarRef}>
        <Toolbar
          sx={{
            paddingLeft: '16px !important',
            paddingRight: '16px !important',
            '& a': {
              textDecoration: 'none',
            },
          }}
        >
          <Button
            sx={{
              color: 'white',
              borderColor: 'white',
              minWidth: '42px',
              padding: 1,
              marginRight: 2,
              display: {
                xs: 'inline',
                lg: 'none',
              },
            }}
            color='inherit'
            variant='outlined'
            disabled={!selectedSite || !isAuthenticated || !isOnDocumentsPage}
            title='Open menu'
          >
            <MenuIcon onClick={() => setIsShowDrawer(true)} />
          </Button>

          {/* Logo */}
          <Box sx={{ display: { xs: 'none', md: 'inline' } }}>
            <Link to={'/'}>
              <img
                style={{
                  width: '200px',
                  marginRight: '40px',
                  marginBottom: '10px',
                }}
                src='./WHC_Logo_REV.png'
                alt='logo'
              />
            </Link>
          </Box>
          <Box sx={{ display: { xs: 'inline', md: 'none' } }}>
            <Link to={'/'}>
              <img
                style={{
                  width: '50px',
                  marginRight: '30px',
                  marginLeft: '10px',
                }}
                src='./logo-2.png'
                alt='logo'
              />
            </Link>
          </Box>

          {/* Text */}
          <Box sx={{ flexGrow: 1 }}>
            <Link to={'/'}>
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 800,
                  color: '#ffffff',
                  display: { xs: 'none', sm: 'inline' },
                }}
              >
                WHITEHAVEN INFORMATION CENTRE
              </Typography>
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 800,
                  color: '#ffffff',
                  display: { xs: 'inline', sm: 'none' },
                }}
              >
                WIC
              </Typography>
            </Link>
          </Box>

          {/* Locations */}
          {isAuthenticated && isOnDocumentsPage && (
            <Box
              sx={{
                display: { xs: 'none', lg: 'flex' },
                flexGrow: 1,
                marginLeft: 2,
              }}
            >
              <Locations componentLocation='nav' />
            </Box>
          )}

          {/* User name */}
          <Typography
            variant='body1'
            component='div'
            ref={usernameEl}
            onClick={() => setIsShowMenu(true)}
            sx={{
              '&:hover': {
                cursor: 'pointer',
                textDecoration: 'underline',
              },
            }}
          >
            {userName}
          </Typography>
          <Menu
            anchorEl={usernameEl.current}
            open={isShowMenu}
            onClose={() => setIsShowMenu(false)}
          >
            <MenuItem onClick={() => logout()}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Drawer
        open={isShowDrawer}
        onClose={() => setIsShowDrawer(false)}
        sx={{
          '& .MuiDrawer-paper': {
            padding: 2,
            backgroundColor: '#ECECF7',
            minWidth: '100vw',
            maxWidth: '465px',
            width: '100vw',
            '@media (min-width:465px)': {
              minWidth: '465px',
              width: '465px',
            },
          },
        }}
      >
        <Box sx={{ textAlign: 'right' }}>
          <IconButton onClick={() => setIsShowDrawer(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Locations componentLocation='drawer' />
        <Sites componentLocation='drawer' />
        <Refiners componentLocation='drawer' />
      </Drawer>
    </Box>
  );
}
